import React, { useState } from "react";
import { 
  Box, FormGroup, FormControlLabel, Switch, Typography, Paper, 
  IconButton, Slider, Button, Accordion, AccordionSummary, 
  AccordionDetails, Alert, TextField 
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useConfig } from "../../contexts/ConfigContext";
import { useUser } from "../../contexts/UserContext";
import MediaViewer from "../media/MediaViewer";
import { fetchFulcrumForm } from "../../utilities/fulcrumLibService";
import { 
  configGenerators,
  generateConfigurationJSON,
  validateConfiguration 
} from "../../utilities/configGenerators";
import { getCacheStatisticsReport, clearCache } from "../../utilities/relatedItemsCache";

interface AdminProps {
  onClose?: () => void;
}

const Admin: React.FC<AdminProps> = ({ onClose }) => {
  const config = useConfig();
  const { profile } = useUser();
  
  // State for configuration generators
  const [isGeneratingComplete, setIsGeneratingComplete] = useState<boolean>(false);
  const [configGenerationStatus, setConfigGenerationStatus] = useState<string>('');
  const [completeGeneratedConfig, setCompleteGeneratedConfig] = useState<string>('');
  
  // State for cache management
  const [cacheStats, setCacheStats] = useState<string>('');

  const handleExaggerationChange = (event: Event, newValue: number | number[]) => {
    config.updateConfig({ terrainExaggeration: newValue as number });
  };

  // Common configuration generation constants
  const CONFIG_GENERATION_DEFAULTS = {
    includeHiddenFields: false,
    prioritizeUserFriendlyLabels: true,
    includeMetadata: false,
    maxPopupFields: 8,
    includeAllForms: true,
    autoDetectRelationshipKeys: true
  };

  const copyCompleteConfigToClipboard = () => {
    navigator.clipboard.writeText(completeGeneratedConfig);
    setConfigGenerationStatus('📋 Complete configuration copied to clipboard!');
    setTimeout(() => setConfigGenerationStatus(''), 3000);
  };

  // Common error handling and validation logic (DRY improvement)
  const handleConfigurationResult = (config: any, successMessage: string) => {
    const validation = validateConfiguration(config);
    
    if (!validation.valid) {
      console.warn('Generated configuration validation warnings:', validation.errors);
      setConfigGenerationStatus(`⚠️ Configuration generated with warnings: ${validation.errors.join(', ')}`);
    } else {
      setConfigGenerationStatus(successMessage);
    }

    const configJson = JSON.stringify(config, null, 2);
    setCompleteGeneratedConfig(configJson);
  };

  // Common API key validation (DRY improvement)
  const validateApiKey = (): boolean => {
    if (!profile?.fulcrum_api_key) {
      setConfigGenerationStatus('❌ No Fulcrum API key available. Please configure your API key.');
      return false;
    }
    return true;
  };

  // Cache management functions
  const updateCacheStats = () => {
    const stats = getCacheStatisticsReport();
    setCacheStats(stats);
  };

  const handleClearCache = () => {
    clearCache();
    setCacheStats('Cache cleared successfully!');
    setTimeout(() => setCacheStats(''), 3000);
  };

  // Generate complete configuration using new generators (DRY refactored)
  const generateCompleteConfiguration = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setCompleteGeneratedConfig('');
    setConfigGenerationStatus('🚀 Generating complete configuration...');

    try {
      setConfigGenerationStatus('📊 Analyzing form fields and generating mappings...');
      
      const completeConfig = await configGenerators.generateComplete(profile, CONFIG_GENERATION_DEFAULTS);

      setConfigGenerationStatus('✅ Validating generated configuration...');
      handleConfigurationResult(completeConfig, '✅ Configuration generated and validated successfully!');

      console.log('🚀 Complete configuration generated:', {
        endpoints: completeConfig.apiEndpoints?.length || 0,
        relationships: Object.keys(completeConfig.reverseRelationships || {}).length,
        totalForms: completeConfig._analysis?.statistics?.totalForms || 0
      });

    } catch (error) {
      console.error('Error generating complete configuration:', error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };

  // Generate field importance analysis only (DRY refactored)
  const generateFieldAnalysis = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setConfigGenerationStatus('📊 Analyzing field importance...');

    try {
      const fieldConfig = await configGenerators.field.autoGenerateConfiguration(profile, {
        userProfile: profile,
        ...CONFIG_GENERATION_DEFAULTS,
        maxPopupFields: 10
      });

      handleConfigurationResult(fieldConfig, '✅ Field analysis completed!');

    } catch (error) {
      console.error('Error generating field analysis:', error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };

  // Generate relationship analysis only (DRY refactored)
  const generateRelationshipAnalysis = async () => {
    if (!validateApiKey()) return;

    setIsGeneratingComplete(true);
    setConfigGenerationStatus('🔗 Analyzing relationships...');

    try {
      const relationshipConfig = await configGenerators.relationship.generateAdminCompatibleConfig(profile);
      
      // For relationship config, we don't need full validation since it's a string
      setCompleteGeneratedConfig(relationshipConfig);
      setConfigGenerationStatus('✅ Relationship analysis completed!');

    } catch (error) {
      console.error('Error generating relationship analysis:', error);
      setConfigGenerationStatus(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsGeneratingComplete(false);
    }
  };



  return (
    <Box sx={{ p: 4 }}>
      <Paper sx={{ p: 30, maxWidth: 600, position: 'relative' }}>
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Typography variant="h5" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
          Application Settings
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center', mb: 3, color: 'text.secondary' }}>
          You can change options here and they should automatically update the behaviour of the application.
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ pr: 5, mt: 2 }}>
          Related Items Cloud Settings
        </Typography>
        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={config.showMediaInRelatedItemsCloud ?? false}
                onChange={(e) => config.updateConfig({ showMediaInRelatedItemsCloud: e.target.checked })}
              />
            }
            label="Show Media in Related Items Cloud"
          />
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={config.showGeoInRelatedItemsCloud ?? false}
                onChange={(e) => config.updateConfig({ showGeoInRelatedItemsCloud: e.target.checked })}
              />
            }
            label="Show Geographic Items in Related Items Cloud"
          />
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={config.showIndirectRelationshipsInRing ?? false}
                onChange={(e) => config.updateConfig({ showIndirectRelationshipsInRing: e.target.checked })}
              />
            }
            label="Show Secondary Relationships in Related Items Cloud"
          />
        </FormGroup>
        
        <Typography variant="h6" gutterBottom sx={{ pr: 5, mt: 4 }}>
          Data Grid Display Settings
        </Typography>
        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={config.hideCardsWithoutImages ?? false}
                onChange={(e) => config.updateConfig({ hideCardsWithoutImages: e.target.checked })}
              />
            }
            label="Hide Cards Without Images"
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 4, display: 'block' }}>
            Only show cards that have primary images for a cleaner presentation
          </Typography>
        </FormGroup>
        
        <Typography variant="h6" gutterBottom sx={{ pr: 5, mt: 4 }}>
          3D Terrain Settings
        </Typography>
        <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
          <Typography id="terrain-exaggeration-slider" gutterBottom>
            Terrain Exaggeration: {config.terrainExaggeration || 4}
          </Typography>
          <Slider
            aria-labelledby="terrain-exaggeration-slider"
            value={config.terrainExaggeration || 4}
            onChange={handleExaggerationChange}
            step={0.5}
            marks
            min={0.5}
            max={8}
            valueLabelDisplay="auto"
          />
        </Box>

        <Typography variant="h6" gutterBottom sx={{ pr: 5, mt: 4 }}>
          Interface Settings
        </Typography>
        <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
          <Typography id="background-opacity-slider" gutterBottom>
            Background Opacity: {Math.round((config.backgroundOpacity || 0.4) * 100)}%
          </Typography>
          <Slider
            aria-labelledby="background-opacity-slider"
            value={config.backgroundOpacity || 0.4}
            onChange={(event: Event, newValue: number | number[]) => 
              config.updateConfig({ backgroundOpacity: newValue as number })
            }
            step={0.01}
            marks={[
              { value: 0.0, label: '0%' },
              { value: 0.2, label: '20%' },
              { value: 0.4, label: '40%' },
              { value: 0.6, label: '60%' },
              { value: 0.8, label: '80%' },
              { value: 1.0, label: '100%' },
            ]}
            min={0.0}
            max={1.0}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            Controls how much the page background overlays the map
          </Typography>
        </Box>

        {/* Developer Tools - Staff Only */}
        {profile?.is_staff && (
          <>
            <Typography variant="h6" gutterBottom sx={{ pr: 5, mt: 4 }}>
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Developer Tools
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
              Tools for developers to analyze and configure the application (Staff Only)
            </Typography>

        {/* Enhanced Configuration Generators */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">🚀 Configuration Generators</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Automatically generate CultureMap configurations from Fulcrum forms with intelligent field analysis and relationship mapping.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={generateCompleteConfiguration}
                disabled={isGeneratingComplete}
                size="small"
              >
                {isGeneratingComplete ? '⏳ Generating...' : '🚀 Complete Config'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={generateFieldAnalysis}
                disabled={isGeneratingComplete}
                size="small"
              >
                📊 Field Analysis
              </Button>
              
              <Button
                variant="outlined"
                onClick={generateRelationshipAnalysis}
                disabled={isGeneratingComplete}
                size="small"
              >
                🔗 Relationships
              </Button>
              
              <Button
                variant="outlined"
                onClick={updateCacheStats}
                size="small"
              >
                📈 Cache Stats
              </Button>
              
              <Button
                variant="outlined"
                color="warning"
                onClick={handleClearCache}
                size="small"
              >
                🗑️ Clear Cache
              </Button>
            </Box>

            {configGenerationStatus && (
              <Alert severity={configGenerationStatus.includes('❌') ? 'error' : 
                              configGenerationStatus.includes('⚠️') ? 'warning' : 'info'} 
                     sx={{ mb: 2 }}>
                {configGenerationStatus}
              </Alert>
            )}

            {cacheStats && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                  {cacheStats}
                </Typography>
              </Alert>
            )}

            {completeGeneratedConfig && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Generated Configuration:</Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={copyCompleteConfigToClipboard}
                  >
                    Copy
                  </Button>
                </Box>
                <TextField
                  multiline
                  rows={20}
                  value={completeGeneratedConfig}
                  fullWidth
                  variant="outlined"
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }
                  }}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
          </>
        )}
        
      </Paper>
      
    </Box>
  );
};

export default Admin;
